import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Chart, ChartDataSets, ChartOptions } from 'chart.js';
import { Label } from 'ng2-charts';
import * as _moment from 'moment';
import html2canvas from 'html2canvas';
import { CampaignService } from 'src/app/services/campaign.service';
import {
    DashboardCapture,
    PdfExportService
} from '../export-campaign-analytics.service';
import { formatUpperCase } from 'src/app/shared/utility/common';
import {
    barChartOptions,
    barDataset,
    formatCompact,
    formatPercent,
    frequencyColours,
    groupedBarChartOptions,
    groupedBarDataset,
    INK,
    lineChartOptions,
    lineDataset,
    MAX_TREND_SERIES,
    SERIES_PALETTE
} from './campaign-results-chart.config';
import {
    AudienceBreakdownRow,
    FrequencyDistributionRow,
    CampaignPerformanceInsights,
    ChannelAudienceInsights,
    emptyInsights,
    InsightsMetric,
    PerformanceInsightRow,
    PerformanceTrendPoint,
    TrendGranularity
} from './campaign-results-insights.model';

const moment = _moment;

/**
 * The grace a panel holding a chart is given before it is photographed.
 *
 * Small because the export turns chart.js animation off for the duration: with nothing to animate
 * a chart is drawn by the time the frame is painted, and the only thing left to absorb is the odd
 * deferred resize. It was 700ms when the animation was still running, which a full run paid forty
 * times over for no gain.
 */
const CHART_REDRAW_MS = 80;

/**
 * How much detail each captured panel is rendered at.
 *
 * Two was sharper and cost the export its life: a full run is around forty panels, and at that
 * scale the encoded images overflow the maximum length of a single string when jsPDF assembles the
 * file. One and a half still reads cleanly at A4 width.
 */
const EXPORT_SCALE = 1.5;

/** High enough that a chart's thin lines stay clean; low enough that forty panels fit in a file. */
const EXPORT_JPEG_QUALITY = 0.92;

/** One line of an Audience Insights table, already read in the metric the dropdown has selected. */
export interface AudienceTableRow {
    label: string;
    value: number;
    /** Share of the table's own total, so the column adds to 100% whichever metric is showing. */
    percent: number;
}

/** An Audience Insights table and the footer figure the design puts under it. */
export interface AudienceTable {
    rows: AudienceTableRow[];
    total: number;
}

/**
 * The DOOH Campaign Results dashboard: what a CPM campaign on the external provider delivered, or —
 * before it finishes — is forecast to deliver.
 *
 * Shown in place of the legacy analytics page, never alongside it. The two answer the same question
 * from different sources: the legacy page reports TMC's own plays and spend, this one reports the
 * provider's audience measurement, and a campaign has only one active metrics source at a time.
 *
 * Everything the provider does not answer renders as a zero rather than disappearing. That is the
 * client's explicit ruling, taken knowing that a zero and an unmeasured figure look alike — which is
 * why {@link insights}.available and {@link insights}.actual are carried through to the wording
 * around the panels rather than only to the figures inside them.
 */
@Component({
    selector: 'app-campaign-results-dashboard',
    templateUrl: './campaign-results-dashboard.component.html',
    styleUrls: ['./campaign-results-dashboard.component.scss']
})
export class CampaignResultsDashboardComponent implements OnChanges {

    @Input() campaignId: string;

    /** The campaign details strip's source — CampaignDetailsDTO, as the overview call returns it. */
    @Input() campaignDetails: any;

    /** Every date the campaign was live on, ascending. Bounds the date-range control. */
    @Input() liveDateRange: string[] = [];

    insights: CampaignPerformanceInsights = emptyInsights();
    isLoading = false;
    hasFailed = false;

    /** Dismissing the forecast bar is per visit: it is a caveat about the figures, not a setting. */
    disclaimerDismissed = false;

    /**
     * The channels every section except Audience Insights is read over. Filtered here rather than
     * server-side: the payload already carries each channel's own figures, so a different selection
     * is a different slice of what is in hand, not another provider call.
     */
    selectedChannelIds: number[] = [];

    /**
     * The metric the three Audience Insights tables are read in.
     *
     * There is no channel selection beside it any more. The delivery endpoint reports no
     * demographics at all — every channel comes back with the same zeroed slices — so a dropdown
     * between them was a choice between identical tables. The panel now describes the campaign.
     */
    audienceMetric: InsightsMetric = 'OTS';
    trendMetric: InsightsMetric = 'OTS';
    granularity: TrendGranularity = 'Day';

    /** Whether the user has overridden the duration-derived default granularity. */
    private granularityPinned = false;

    grpView: 'City' | 'Channel' = 'City';

    /** The dashboard's own date range, which is the campaign's whole run until it is narrowed. */
    dateForm: FormGroup;

    readonly metrics: InsightsMetric[] = ['OTS', 'LTS', 'Reach'];
    readonly granularities: TrendGranularity[] = ['Day', 'Week', 'Month'];

    /**
     * Where the default over-time bucket changes. A fortnight of days is readable; a quarter of them
     * is a smear, and a year of weeks is no better — so the default follows the campaign's length
     * and the toggle stays free to override it.
     */
    private static readonly DAILY_UP_TO_DAYS = 14;
    private static readonly WEEKLY_UP_TO_DAYS = 90;

    readonly tooltips = {
        universe: 'Total target audience size.',
        ots: 'Total opportunities to see the campaign.',
        lts: 'Total likely-to-see contacts.',
        grp: 'OTS relative to the target audience Universe.',
        // T38016 words this "for the selected channel". There is no channel selection on this panel
        // any more, so the tooltip would be describing a control that is not there.
        audienceInsights:
            'View OTS or LTS by age, gender, and income across the campaign.',
        performanceOverTime: 'View the selected metric over the campaign period.',
        performanceByChannel: 'Compare performance across channels.',
        reachByChannel:
            'View how many people were reached 1+, 2+, 3+ times, etc. by channel.',
        grpByCity: 'View GRP by city or channel.',
        channel: 'Select a channel.',
        metric: 'Select OTS, LTS, or Reach.',
        cityChannelToggle: 'Choose how to view GRP.'
    };

    // ---- what the panels draw ---------------------------------------------------------------
    //
    // Held as fields rather than computed by getters in the template: ng2-charts redraws whenever
    // the array bound to it is a new object, and a getter hands it one on every change-detection
    // pass. They are rebuilt from `insights` whenever something that narrows them changes — the
    // channel filter, a metric, the granularity or the date range — and never more often.

    trendChartLabels: Label[] = [];
    trendChartData: ChartDataSets[] = [];
    readonly trendChartOptions: ChartOptions = lineChartOptions(formatCompact);

    /** The over-time figures as a table — the only way to read an exact value off that panel. */
    trendTableVisible = false;

    channelChartLabels: Label[] = [];
    otsByChannelData: ChartDataSets[] = [];
    ltsByChannelData: ChartDataSets[] = [];
    grpByChannelData: ChartDataSets[] = [];
    // One options object per canvas: chart.js merges what it is handed into the chart it builds,
    // so two charts sharing one object share whatever the first of them did to it.
    readonly otsBarOptions: ChartOptions = barChartOptions(formatCompact);
    readonly ltsBarOptions: ChartOptions = barChartOptions(formatCompact);
    readonly grpBarOptions: ChartOptions = barChartOptions(formatPercent);
    readonly grpByCityOptions: ChartOptions = barChartOptions(formatPercent);

    reachByChannelLabels: Label[] = [];
    reachByChannelData: ChartDataSets[] = [];
    reachByChannelOptions: ChartOptions;

    /** The share each Reach N+ bar represents, by [rung][channel], for that chart's tooltip. */
    private reachByChannelShares: number[][] = [];

    grpByCityLabels: Label[] = [];
    grpByCityData: ChartDataSets[] = [];

    ageTable: AudienceTable = { rows: [], total: 0 };
    genderTable: AudienceTable = { rows: [], total: 0 };
    incomeTable: AudienceTable = { rows: [], total: 0 };

    /** True while the export is stepping the page through its combinations. */
    isExporting = false;

    /**
     * Shown when an export did not produce a file (T39732).
     *
     * The run restores every control it moved in its `finally`, so without this the page comes back
     * looking exactly as it did before the click and the failure reads as the button doing nothing.
     */
    exportFailed = false;

    constructor(private campaignService: CampaignService, private formBuilder: FormBuilder,
                private pdfService: PdfExportService, private host: ElementRef,
                private changeDetector: ChangeDetectorRef, private zone: NgZone) {
        this.dateForm = this.formBuilder.group({
            start: new FormControl(null),
            end: new FormControl(null)
        });
        // The range narrows the per-day series and nothing else — it is the only control on the
        // page whose changes arrive through a form rather than through a handler.
        this.dateForm.valueChanges.subscribe(() => this.buildTrendChart());
        this.reachByChannelOptions = groupedBarChartOptions(
            formatCompact,
            (rung, channel) => this.reachShareSuffix(rung, channel)
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.liveDateRange && this.liveDateRange && this.liveDateRange.length) {
            this.resetDateRange();
        }
        // A new campaign means a new buy: the channel filter and the figures belong to the campaign
        // that was showing, so neither survives the change.
        if (changes.campaignId && this.campaignId) {
            this.selectedChannelIds = [];
            this.disclaimerDismissed = false;
            this.granularityPinned = false;
            this.load();
        }
    }

    // ---- the header -------------------------------------------------------------------------

    get isCompleted(): boolean {
        return this.campaignDetails && this.campaignDetails.status === 'COMPLETED';
    }

    get subtitle(): string {
        return this.isCompleted
            ? 'Post-campaign performance overview'
            : 'Estimated campaign performance';
    }

    get showForecastDisclaimer(): boolean {
        return !this.isCompleted && !this.disclaimerDismissed;
    }

    /**
     * Whether the figures on the page were measured rather than forecast. Drives the wording around
     * the empty panels: a zero against a measured campaign is the provider not reporting a
     * breakdown, which is a different statement from an audience of nobody.
     */
    get isActual(): boolean {
        return this.insights.actual;
    }

    get minDate(): string {
        return this.liveDateRange && this.liveDateRange.length ? this.liveDateRange[0] : null;
    }

    get maxDate(): string {
        return this.liveDateRange && this.liveDateRange.length
            ? this.liveDateRange[this.liveDateRange.length - 1]
            : null;
    }

    /**
     * The whole dashboard as a PDF, with no filter left applied.
     *
     * A PDF has no dropdowns, so every one of them is spent instead: each panel is captured once
     * per combination of its own controls, and the channel filter is opened to the whole buy first.
     * The page drives itself through those combinations rather than a second, hidden copy of the
     * template being maintained alongside it — a copy would drift, and the charts have to be real
     * canvases for the capture to contain anything.
     *
     * The granularity toggle is included: it changes the series, not just its resolution, once
     * days are bucketed. Every selection is put back afterwards, including on failure.
     */
    async exportPdf() {
        if (this.isExporting) {
            return;
        }
        const before = {
            audienceMetric: this.audienceMetric,
            trendMetric: this.trendMetric,
            granularity: this.granularity,
            grpView: this.grpView,
            selectedChannelIds: [...this.selectedChannelIds],
            trendTableVisible: this.trendTableVisible
        };

        this.isExporting = true;
        // Cleared on every attempt, so a retry that works does not leave the last failure's notice
        // standing over a file that downloaded.
        this.exportFailed = false;
        // Outside Angular's zone for the whole run. html2canvas and the settle delay schedule
        // timers and promises of their own, and inside the zone each one triggers a change
        // detection pass — landing between a selection being changed and this code checking the
        // view itself, which is exactly the "expression changed after it was checked" that the
        // first version of this threw on the trend chart. Out here the only passes that run are
        // the explicit ones below.
        // Detached for the duration, and driven by the explicit detectChanges() calls below.
        //
        // The run walks the page through every combination of its own controls, so between any two
        // captures its bound state is deliberately mid-flight. Angular's development build follows
        // each change detection pass with a second, verifying pass that asserts nothing moved since
        // the first — and these changes move it on purpose, which is what raised
        // "expression changed after it was checked" on the trend chart's datasets. A detached view
        // is not visited by either pass; detectChanges() still refreshes it on demand, which is all
        // the capture needs.
        this.changeDetector.detach();
        // Animation off for the run. Every capture would otherwise wait out a redraw it has no
        // reason to watch, and a full export is dozens of captures.
        const animation = Chart.defaults.global.animation.duration;
        Chart.defaults.global.animation.duration = 0;
        await this.zone.runOutsideAngular(async () => {
            try {
                // No filters: the export describes the campaign, not one reading of it.
                this.selectedChannelIds = this.channels.map(channel => channel.channelId);
                this.trendTableVisible = false;
                this.rebuildAll();

                const captures: DashboardCapture[] = [];

                for (const panel of ['Campaign details', 'Headline figures', 'Spend']) {
                    await this.capture(captures, panel, '');
                }

                // One capture per metric, not per channel per metric: the panel describes the
                // campaign now, so a channel loop would export the same table several times over.
                for (const metric of this.metrics) {
                    this.audienceMetric = metric;
                    this.buildAudienceTables();
                    await this.capture(captures, 'Audience insights', metric);
                }

                for (const metric of this.metrics) {
                    for (const bucket of this.granularities) {
                        this.trendMetric = metric;
                        this.granularity = bucket;
                        this.buildTrendChart();
                        await this.capture(captures, 'Performance over time',
                            `${metric} · by ${bucket}`);
                    }
                }

                await this.capture(captures, 'Performance by channel', 'All channels');
                await this.capture(captures, 'Reach N+ by channel', 'All channels');

                for (const view of ['City', 'Channel'] as ('City' | 'Channel')[]) {
                    this.grpView = view;
                    this.buildGrpByCityChart();
                    await this.capture(captures, 'GRP by city', `By ${view}`);
                }

                await this.capture(captures, 'Important notes', '');

                await this.pdfService.exportResultsDashboard(captures, {
                    campaignName: this.campaignDetails && this.campaignDetails.title
                        ? this.campaignDetails.title : 'Campaign',
                    period: this.subtitle,
                    channels: this.channels.map(channel => channel.label).join(', ') || '—'
                });
            } catch (failed) {
                // Swallowed on purpose rather than rethrown. Nothing above this catches it — the
                // click handler is the top of the stack — so an unhandled rejection is all the user
                // would get, and the finally below puts the page back exactly as it was. The notice
                // is the only thing that distinguishes a failed export from a button that did
                // nothing. Kept on the console for whoever is looking.
                console.error('[campaign-results] PDF export failed', failed);
                this.exportFailed = true;
            } finally {
                this.audienceMetric = before.audienceMetric;
                this.trendMetric = before.trendMetric;
                this.granularity = before.granularity;
                this.grpView = before.grpView;
                this.selectedChannelIds = before.selectedChannelIds;
                this.trendTableVisible = before.trendTableVisible;
                Chart.defaults.global.animation.duration = animation;
                this.isExporting = false;
                // Reattached before the final refresh, so the page goes back to being checked with
                // every other component and the button leaves its "Preparing PDF…" state.
                this.changeDetector.reattach();
                this.zone.run(() => this.rebuildAll());
            }
        });
    }

    /**
     * One panel, as it currently stands, appended to the run.
     *
     * Waits for Angular to write the new state into the DOM and for chart.js to finish redrawing
     * before the screenshot: a canvas captured mid-animation holds a half-drawn chart, and there is
     * no event to wait on that covers every chart on the page.
     */
    private async capture(into: DashboardCapture[], panel: string, combination: string) {
        const element = this.host.nativeElement
            .querySelector(`[data-export="${panel}"]`) as HTMLElement;
        if (!element) {
            return;
        }
        this.changeDetector.detectChanges();
        // A painted frame is all a table needs. Only a panel that actually holds a chart pays the
        // extra grace — and most of them do not: of the ten panels, six draw nothing but tables.
        await new Promise(resolve =>
            requestAnimationFrame(() => requestAnimationFrame(() => resolve(null))));
        if (element.querySelector('canvas')) {
            await new Promise(resolve => setTimeout(resolve, CHART_REDRAW_MS));
        }

        const canvas = await html2canvas(element, {
            scale: EXPORT_SCALE,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false
        });
        into.push({
            panel,
            combination,
            image: canvas.toDataURL('image/jpeg', EXPORT_JPEG_QUALITY),
            widthPx: canvas.width,
            heightPx: canvas.height
        });
    }

    /** Every chart and table rebuilt from the current selections. */
    private rebuildAll() {
        this.buildAudienceTables();
        this.buildTrendChart();
        this.buildChannelCharts();
        this.buildReachByChannelChart();
        this.buildGrpByCityChart();
        this.changeDetector.detectChanges();
    }

    // ---- the campaign details strip ---------------------------------------------------------

    get pricingModel(): string {
        // Only PER_IMPRESSION campaigns reach this page at all — the provider is not a source for
        // a per-hour buy — but the value is read rather than hard-coded so the strip cannot lie.
        return this.campaignDetails && this.campaignDetails.billingMethod === 'PER_HOUR'
            ? 'CPH'
            : 'CPM';
    }

    get statusLabel(): string {
        return this.campaignDetails && this.campaignDetails.status
            ? formatUpperCase(this.campaignDetails.status)
            : '';
    }

    /** The status dot's colour class, matching the legacy analytics page's vocabulary. */
    get statusClass(): string {
        return this.statusLabel.toLowerCase().replace(' ', '-');
    }

    get channelCount(): number {
        return this.insights.byChannel.length;
    }

    get screenCount(): number {
        return this.campaignDetails && this.campaignDetails.screens
            ? this.campaignDetails.screens
            : 0;
    }

    // ---- the KPI and money tiles ------------------------------------------------------------

    get channels(): PerformanceInsightRow[] {
        return this.insights.byChannel;
    }

    get selectedChannels(): PerformanceInsightRow[] {
        return this.channels.filter(channel => this.selectedChannelIds.includes(channel.channelId));
    }

    get allChannelsSelected(): boolean {
        return this.channels.length > 0 && this.selectedChannelIds.length === this.channels.length;
    }

    /**
     * The target audience, campaign-wide, whatever the channel filter says.
     *
     * A universe is de-duplicated across the channels that share it, so the channels' own
     * populations cannot be added up — and narrowing the channel filter does not shrink the
     * audience the campaign was bought against. GRP below is read against this figure, which is
     * what the design's own tooltip says GRP means.
     */
    get universe(): number {
        return this.insights.total ? this.insights.total.population : 0;
    }

    /** OTS and LTS are summable across channels, which is the only reason the filter can be local. */
    get ots(): number {
        return this.selectedChannels.reduce((sum, channel) => sum + channel.impressions, 0);
    }

    get lts(): number {
        return this.selectedChannels.reduce((sum, channel) => sum + channel.attentiveImpressions, 0);
    }

    /** Recomputed rather than summed: GRP is a ratio, and ratios do not add. */
    get grp(): number {
        return this.universe > 0 ? (this.ots / this.universe) * 100 : 0;
    }

    get totalBudget(): number {
        return this.spend + this.remainingBudget;
    }

    get spend(): number {
        return this.priceValue(this.campaignDetails && this.campaignDetails.spentBalance);
    }

    get remainingBudget(): number {
        return this.priceValue(this.campaignDetails && this.campaignDetails.unspentBalance);
    }

    get currencyCode(): string {
        const balance = this.campaignDetails && this.campaignDetails.spentBalance;
        return balance && balance.currencyCode ? balance.currencyCode : '';
    }

    private priceValue(price: any): number {
        return price && price.value != null ? price.value : 0;
    }

    // ---- audience insights ------------------------------------------------------------------

    /**
     * Whether the selected metric can be added up across channels.
     *
     * OTS and LTS are contact counts and sum. Reach does not: the provider de-duplicates its
     * audience, so one person standing in front of two of the campaign's screens is one reach and
     * not two, and adding the channels' figures would count them twice. With Reach selected the
     * tables therefore say so rather than printing a total nobody measured.
     */
    get audienceSummable(): boolean {
        return this.audienceMetric !== 'Reach';
    }

    /** One channel's audience insights out of the payload, or null when it does not carry them. */
    private audienceOf(channelId: number): ChannelAudienceInsights {
        const all = this.insights.channelAudiences || [];
        return all.find(entry => entry.channelId === channelId) || null;
    }

    /**
     * The campaign's audience, as the sum of its channels'.
     *
     * Rows are matched by label rather than by position: the provider returns a channel's bands
     * unordered, and two channels need not list them the same way round.
     */
    private campaignAudienceOf(
        section: (channel: ChannelAudienceInsights) => AudienceBreakdownRow[]
    ): AudienceBreakdownRow[] {
        const channels = this.insights.channelAudiences && this.insights.channelAudiences.length
            ? this.insights.channelAudiences
            : [this.insights.selectedChannel].filter(Boolean);

        const byLabel = new Map<string, AudienceBreakdownRow>();
        for (const channel of channels) {
            for (const row of section(channel) || []) {
                const running = byLabel.get(row.label);
                if (!running) {
                    byLabel.set(row.label, { ...row });
                    continue;
                }
                running.impressions = (running.impressions || 0) + (row.impressions || 0);
                running.attentiveImpressions =
                    (running.attentiveImpressions || 0) + (row.attentiveImpressions || 0);
                // Summed so the row carries something, but never shown: audienceSummable gates
                // Reach out of the table entirely. Left here so a future campaign-level reach from
                // the provider has a field to land in rather than a gap.
                running.reach = (running.reach || 0) + (row.reach || 0);
            }
        }
        return Array.from(byLabel.values());
    }

    onAudienceMetricChange(metric: InsightsMetric) {
        this.audienceMetric = metric;
        // Only the three tables read this dropdown; the charts below have their own.
        this.buildAudienceTables();
    }

    /** The one figure of a breakdown row the Metric dropdown has selected. */
    metricValueOf(row: { impressions: number; attentiveImpressions: number; reach: number }): number {
        if (!row) {
            return 0;
        }
        if (this.audienceMetric === 'LTS') {
            return row.attentiveImpressions || 0;
        }
        if (this.audienceMetric === 'Reach') {
            return row.reach || 0;
        }
        return row.impressions || 0;
    }

    // ---- the channel filter -----------------------------------------------------------------

    toggleAllChannels() {
        this.selectedChannelIds = this.allChannelsSelected
            ? []
            : this.channels.map(channel => channel.channelId);
        this.buildFilteredPanels();
    }

    onChannelFilterChange(channelIds: number[]) {
        this.selectedChannelIds = channelIds || [];
        this.buildFilteredPanels();
    }

    /**
     * Whether the figures below the filter cover fewer channels than the campaign bought. The GRP
     * by City panel says so when it does: the payload gives city totals for the whole campaign and
     * no way to attribute a city to a channel, so that one panel cannot honour the filter.
     */
    get isChannelSubset(): boolean {
        return this.channels.length > 0 && this.selectedChannelIds.length < this.channels.length;
    }

    // ---- performance over time --------------------------------------------------------------

    onGranularityChange(granularity: TrendGranularity) {
        this.granularity = granularity;
        // Pinned from here on: a campaign's length does not change under the user, so a manual
        // choice should survive the next load rather than being argued with.
        this.granularityPinned = true;
        this.buildTrendChart();
    }

    onTrendMetricChange(metric: InsightsMetric) {
        this.trendMetric = metric;
        this.buildTrendChart();
    }

    onGrpViewChange(view: 'City' | 'Channel') {
        this.grpView = view;
        this.buildGrpByCityChart();
    }

    /**
     * The daily points the chart draws, narrowed to the selected channels and the chosen dates.
     * Bucketing them into weeks or months is the chart's own job — the backend sends days only.
     */
    get filteredTrendPoints(): PerformanceTrendPoint[] {
        const start = this.dateForm.get('start').value;
        const end = this.dateForm.get('end').value;
        return this.insights.perChannelPerDay.filter(point => {
            if (!this.selectedChannelIds.includes(point.channelId)) {
                return false;
            }
            if (!start || !end) {
                return true;
            }
            const date = moment(point.date);
            return date.isSameOrAfter(moment(start), 'day')
                && date.isSameOrBefore(moment(end), 'day');
        });
    }

    // ---- reach N+ by audience type ----------------------------------------------------------

    // ---- building the panels ----------------------------------------------------------------

    /** Everything the panels draw. Called once a payload lands, never from the template. */
    private buildPanels() {
        this.buildAudienceTables();
        this.buildFilteredPanels();
    }

    /**
     * The panels the channel multi-select narrows: over time, by channel, Reach N+ by channel and
     * GRP. Audience Insights and Reach N+ by audience type are deliberately not among them — both
     * describe one channel's own audience, which is why they have their own dropdown.
     */
    private buildFilteredPanels() {
        this.buildTrendChart();
        this.buildChannelCharts();
        this.buildReachByChannelChart();
        this.buildGrpByCityChart();
    }

    // 4. Audience insights

    private buildAudienceTables() {
        this.ageTable = this.audienceTable(
            this.campaignAudienceOf(channel => channel.ageDistribution), true);
        this.genderTable = this.audienceTable(
            this.campaignAudienceOf(channel => channel.genderDistribution), false);
        this.incomeTable = this.audienceTable(
            this.campaignAudienceOf(channel => channel.incomeDistribution), false);
    }

    /**
     * One table, read in the selected metric.
     *
     * The percentage is recomputed against the column's own total rather than taken from the row's
     * `percentOfAudience`, which is a share of reach: with the Metric dropdown on OTS the column
     * would otherwise be impressions beside percentages of something else, and would not add up to
     * the 100.0% the design's footer promises.
     */
    private audienceTable(rows: AudienceBreakdownRow[], sortByAgeBand: boolean): AudienceTable {
        const ordered = sortByAgeBand
            ? rows.slice().sort((a, b) => this.ageLowerBound(a.label) - this.ageLowerBound(b.label))
            : rows.slice();
        // metricValueOf already answers 0 for a null reach, which the provider does send.
        const values = ordered.map(row => this.metricValueOf(row));
        const total = values.reduce((sum, value) => sum + value, 0);
        return {
            rows: ordered.map((row, index) => ({
                label: row.label,
                value: values[index],
                percent: total > 0 ? (values[index] / total) * 100 : 0
            })),
            total
        };
    }

    /** "6 – 17" before "18 – 29" before "60+" — the provider returns the bands unsorted. */
    private ageLowerBound(label: string): number {
        const match = /\d+/.exec(label || '');
        // A band with no number in its label sorts last rather than jumping to the front of the age
        // range, where it would read as the youngest.
        return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
    }

    /**
     * The frequency ladder a set of curves share.
     *
     * Every curve in one payload carries the same frequencies in the same order, so a rung is read
     * by position; the longest curve supplies the labels, and a curve that is short at the end is
     * padded with zeroes rather than cutting the table down to it. Nothing stops at the first zero
     * either — the provider can answer 1+ and 3+ but not 2+, and that gap is a figure of zero
     * sitting between two real ones, not the end of the curve.
     */
    private ladderOf(curves: FrequencyDistributionRow[][]): FrequencyDistributionRow[] {
        return curves.reduce(
            (longest, curve) => (curve.length > longest.length ? curve : longest),
            [] as FrequencyDistributionRow[]
        );
    }

    // 6. Performance over time

    private buildTrendChart() {
        const buckets = new Map<string, string>();
        const byChannel = new Map<number, { label: string; totals: Map<string, number> }>();

        this.filteredTrendPoints.forEach(point => {
            const key = this.bucketKey(point.date);
            if (!buckets.has(key)) {
                buckets.set(key, this.bucketLabel(point.date));
            }
            let channel = byChannel.get(point.channelId);
            if (!channel) {
                channel = { label: point.label, totals: new Map<string, number>() };
                byChannel.set(point.channelId, channel);
            }
            // Only impressions are summed into a bucket. Reach and GRP are not summable over days
            // — the page's own footnote says so — which is why neither reaches this chart.
            channel.totals.set(key, (channel.totals.get(key) || 0) + this.trendValueOf(point));
        });

        // The keys are ISO dates, so lexical order is chronological order.
        const keys = Array.from(buckets.keys()).sort();
        this.trendChartLabels = keys.map(key => buckets.get(key));

        const series = Array.from(byChannel.keys()).map(channelId => {
            const channel = byChannel.get(channelId);
            const values = keys.map(key => channel.totals.get(key) || 0);
            return {
                channelId,
                label: channel.label,
                values,
                total: values.reduce((sum, value) => sum + value, 0)
            };
        });

        this.trendChartData = this.foldTrendSeries(series, keys.length)
            .map(line => lineDataset(line.label, line.values, line.colour));
    }

    /**
     * Caps the chart at the palette's slots, folding the smallest channels into one line.
     *
     * Past eight series the only way to add another is to invent a ninth colour, which is
     * indistinguishable from one of the eight to a colour-blind reader. Folding is honest here
     * because OTS and LTS add up across channels; the fold is a real total, drawn in grey so it
     * does not pass for a channel of its own.
     */
    private foldTrendSeries(
        series: { channelId: number; label: string; values: number[]; total: number }[],
        bucketCount: number
    ): { label: string; values: number[]; colour: string }[] {
        let drawn = series;
        let folded: typeof series = [];
        if (series.length > MAX_TREND_SERIES) {
            const ranked = series.slice().sort((a, b) => b.total - a.total);
            drawn = ranked.slice(0, MAX_TREND_SERIES - 1);
            folded = ranked.slice(MAX_TREND_SERIES - 1);
        }

        const colours = this.channelColours(drawn.map(line => line.channelId));
        const lines = drawn.map((line, index) => ({
            label: line.label,
            values: line.values,
            colour: colours[index]
        }));

        if (folded.length) {
            const totals: number[] = [];
            for (let bucket = 0; bucket < bucketCount; bucket++) {
                totals.push(folded.reduce((sum, line) => sum + line.values[bucket], 0));
            }
            lines.push({
                label: 'Other (' + folded.length + ' channels)',
                values: totals,
                colour: INK.muted
            });
        }
        return lines;
    }

    /**
     * A colour per channel, taken from the channel's place in the buy rather than from its rank in
     * this chart: narrowing the filter must not repaint the channels that survive it. Two channels
     * can only want the same slot when the buy is bigger than the palette, and the walk moves the
     * second along rather than letting two lines share a colour.
     */
    private channelColours(channelIds: number[]): string[] {
        const taken = new Set<number>();
        return channelIds.map(channelId => {
            const home = this.channels.findIndex(channel => channel.channelId === channelId);
            let slot = (home < 0 ? 0 : home) % SERIES_PALETTE.length;
            while (taken.has(slot) && taken.size < SERIES_PALETTE.length) {
                slot = (slot + 1) % SERIES_PALETTE.length;
            }
            taken.add(slot);
            return SERIES_PALETTE[slot];
        });
    }

    /**
     * Reach has no daily figure to report — it counts people, and one person on two days is still
     * one person — so the metric reads zero on this panel by the nature of the measure rather than
     * by anything missing from the payload.
     */
    private trendValueOf(point: PerformanceTrendPoint): number {
        if (this.trendMetric === 'LTS') {
            return point.attentiveImpressions || 0;
        }
        if (this.trendMetric === 'Reach') {
            return 0;
        }
        return point.impressions || 0;
    }

    private bucketKey(date: string): string {
        const day = moment(date);
        if (this.granularity === 'Month') {
            return day.startOf('month').format('YYYY-MM-DD');
        }
        if (this.granularity === 'Week') {
            return day.startOf('isoWeek').format('YYYY-MM-DD');
        }
        return day.format('YYYY-MM-DD');
    }

    private bucketLabel(date: string): string {
        const day = moment(date);
        if (this.granularity === 'Month') {
            return day.format('MMM YYYY');
        }
        if (this.granularity === 'Week') {
            return 'w/c ' + day.startOf('isoWeek').format('DD MMM');
        }
        return day.format('DD MMM');
    }

    // 7. Performance by channel

    private buildChannelCharts() {
        const channels = this.selectedChannels;
        this.channelChartLabels = channels.map(channel => channel.label);
        this.otsByChannelData = [barDataset('Total OTS', channels.map(c => c.impressions || 0))];
        this.ltsByChannelData = [
            barDataset('Total LTS', channels.map(c => c.attentiveImpressions || 0))
        ];
        this.grpByChannelData = [barDataset('GRP', channels.map(c => c.grp || 0))];
    }

    // 8. Reach N+ by channel

    private buildReachByChannelChart() {
        const channels = this.insights.frequencyByChannel
            .filter(channel => this.selectedChannelIds.includes(channel.channelId));
        this.reachByChannelLabels = channels.map(channel => channel.label);

        const rungs = this.ladderOf(channels.map(channel => channel.frequencyDistribution));
        const colours = frequencyColours(rungs.length);

        // Headcounts are plotted and the share goes to the tooltip: a reader comparing channels
        // needs their scale, and a channel's share of its own audience says nothing about how many
        // people that is.
        this.reachByChannelShares = rungs.map((unused, rung) =>
            channels.map(channel => {
                const row = channel.frequencyDistribution[rung];
                return row ? row.percentOfAudience || 0 : 0;
            }));

        this.reachByChannelData = rungs.map((rung, index) => groupedBarDataset(
            rung.frequency,
            channels.map(channel => {
                const row = channel.frequencyDistribution[index];
                return row ? row.audience || 0 : 0;
            }),
            colours[index]
        ));
    }

    private reachShareSuffix(rung: number, channel: number): string {
        const shares = this.reachByChannelShares[rung];
        return ' (' + formatPercent(shares ? shares[channel] : 0) + ')';
    }

    // 9. GRP by city

    private buildGrpByCityChart() {
        // City rows carry no channel, so the multi-select cannot narrow them — the panel says so
        // rather than quietly showing campaign-wide cities beside a filtered set of channels.
        const rows = this.grpView === 'Channel' ? this.selectedChannels : this.insights.byCity;
        this.grpByCityLabels = rows.map(row => row.label);
        this.grpByCityData = [barDataset('GRP', rows.map(row => row.grp || 0))];
    }

    /** What an empty panel says about its zeroes, which is not the same statement in both cases. */
    get zeroNote(): string {
        return this.isActual
            ? 'Not reported by the provider for this campaign — shown as zero.'
            : 'Nothing forecast for this selection — shown as zero.';
    }

    // ---- the summary bar --------------------------------------------------------------------

    /**
     * The one-line narrative at the foot of the page. Worded off {@link isActual} so a forecast is
     * never read back as a result.
     */
    get summaryNarrative(): string {
        const verb = this.isActual ? 'delivered' : 'is estimated to deliver';
        const channels = this.selectedChannels.length;
        const channelWord = channels === 1 ? 'channel' : 'channels';
        return `This campaign ${verb} ${this.format(this.ots)} OTS and ${this.format(this.lts)} LTS `
            + `across ${channels} ${channelWord}, against a universe of ${this.format(this.universe)} `
            + `people (${this.grp.toFixed(1)}% GRP).`;
    }

    private format(value: number): string {
        return (value || 0).toLocaleString('en-US');
    }

    // ---- loading ----------------------------------------------------------------------------

    private load() {
        if (!this.campaignId) {
            return;
        }
        this.isLoading = true;
        this.hasFailed = false;
        this.campaignService
            // No channel asked for: the payload carries every channel's audience, and the panel
            // sums them rather than showing one.
            .getCampaignDeliveredInsights(this.campaignId, null)
            .subscribe(
                (response: CampaignPerformanceInsights) => {
                    // Zeroes rather than null when the provider has nothing: every panel still
                    // draws itself, which is the ruling this page was built to.
                    this.insights = response && response.available ? response : emptyInsights();
                    this.applySelections();
                    this.isLoading = false;
                },
                () => {
                    this.insights = emptyInsights();
                    this.applySelections();
                    this.isLoading = false;
                    this.hasFailed = true;
                }
            );
    }

    /**
     * Re-points the channel filter at what actually came back.
     *
     * It starts as the whole buy, which is the only selection that makes the KPI tiles agree with
     * the campaign total.
     */
    private applySelections() {
        const known = this.channels.map(channel => channel.channelId);
        const kept = this.selectedChannelIds.filter(id => known.includes(id));
        this.selectedChannelIds = kept.length ? kept : known;
        this.applyDefaultGranularity();
        this.buildPanels();
    }

    private resetDateRange() {
        this.dateForm.setValue(
            { start: this.minDate, end: this.maxDate },
            { emitEvent: false }
        );
        this.applyDefaultGranularity();
        // The default bucket may have changed with the range, and the chart is bucketed by it.
        this.buildTrendChart();
    }

    /** Days, weeks or months by how long the campaign ran — until the user says otherwise. */
    private applyDefaultGranularity() {
        if (this.granularityPinned) {
            return;
        }
        const days = this.liveDateRange ? this.liveDateRange.length : 0;
        if (days > CampaignResultsDashboardComponent.WEEKLY_UP_TO_DAYS) {
            this.granularity = 'Month';
        } else if (days > CampaignResultsDashboardComponent.DAILY_UP_TO_DAYS) {
            this.granularity = 'Week';
        } else {
            this.granularity = 'Day';
        }
    }
}
